import { useState } from "react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "@/api/firebase/firebase.config";

export const useSendEmailVerification = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [verificationSent, setVerificationSent] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);

  const sendEmail = async () => {
    setLoading(true);
    setError(null);

    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setVerificationSent(true);
        console.log("Email verification sent");
        return true;
      } else {
        throw new Error("No authenticated user");
      }
    } catch (err) {
      const error = err as Error;
      console.error("Error sending verification:", error);
      setError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if verification completed
  const checkVerificationStatus = async () => {
    setLoading(true);
    setError(null);

    if (!auth.currentUser) {
      setError(new Error("No authenticated user"));
      setIsVerified(false);
      setLoading(false);
      return false;
    }

    try {
      // Force refresh the user to get latest emailVerified status
      await auth.currentUser.reload();

      const verified = auth.currentUser.emailVerified;
      setIsVerified(verified);

      if (!verified) {
        setError(
          new Error(
            "Email not verified yet. Please check your inbox and click the verification link."
          )
        );
      }

      setLoading(false);
      return verified;
    } catch (err) {
      console.error("Error checking verification status:", err);
      setError(
        new Error("Failed to check verification status. Please try again.")
      );
      setIsVerified(false);
      setLoading(false);
      return false;
    }
  };

  return {
    loading,
    error,
    verificationSent,
    isVerified,
    sendEmail,
    checkVerificationStatus,
  };
};
